/*
  Alle Gespräche mit dem Server an einer Stelle.

  Bewusst ohne zusätzliche Bibliothek: Gebraucht werden Anmelden, Registrieren,
  Profil ändern und Dateien — das sind rund zehn Aufrufe, die der Browser mit
  `fetch` von Haus aus beherrscht. Ein Paket dafür wäre eine Abhängigkeit, die
  später gepflegt werden müsste.

  Was hier NICHT herkommt: Meinungsbilder, Aufgaben, Teilnahmezusagen und die
  Fraktionssitzung. Das erzeugt die Fraktion selbst und liegt im Prototyp noch
  im Browser. Sitzung, Tagesordnung und Vorlagen kommen von hier — der Server
  hat sie über die OParl-Schnittstelle aus dem Ratsinformationssystem geholt.
  Markierungen und Notizen ebenfalls; der Chat läuft getrennt über Matrix.
*/

/*
  Beim Entwickeln läuft die App auf dem eigenen Rechner, der Server steht
  woanders. Im Betrieb liegen beide unter derselben Adresse — dann genügt ein
  leerer Vorsatz, und der Browser fragt dort, wo er ohnehin ist.
*/
const AUF_EIGENEM_RECHNER =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

export const SERVER = AUF_EIGENEM_RECHNER ? "https://demo.xn--fraktionsbro-mlb.de" : "";

const SPEICHERSCHLUESSEL = "fraktionsbuero-anmeldung-v1";

/* ---------------------------------------------------------------
   Anmeldung merken
   --------------------------------------------------------------- */

export function gemerkteAnmeldung() {
  try {
    const roh = window.localStorage.getItem(SPEICHERSCHLUESSEL);
    if (!roh) return null;
    const daten = JSON.parse(roh);
    return daten && daten.token && daten.konto ? daten : null;
  } catch {
    return null;
  }
}

function merke(token, konto) {
  try {
    window.localStorage.setItem(SPEICHERSCHLUESSEL, JSON.stringify({ token, konto }));
  } catch {
    /* privater Modus — dann eben nur für diese Sitzung */
  }
}

export function vergissAnmeldung() {
  try {
    window.localStorage.removeItem(SPEICHERSCHLUESSEL);
  } catch {
    /* nichts zu tun */
  }
  /* Das Dateitoken gehört zur Anmeldung und geht mit ihr. */
  vergissDateiZugang();
}

/* ---------------------------------------------------------------
   Grundlage
   --------------------------------------------------------------- */

/*
  Der Server antwortet in zwei Formen: unsere eigenen Endpunkte melden
  { fehler: "..." }, die eingebauten von PocketBase { message, data }.
  Hier wird beides zu einer verständlichen deutschen Meldung.
*/
function meldungAus(antwort, inhalt) {
  if (inhalt && inhalt.fehler) return inhalt.fehler;

  if (inhalt && inhalt.data && typeof inhalt.data === "object") {
    const erstes = Object.values(inhalt.data)[0];
    if (erstes && erstes.message) return erstes.message;
  }

  if (antwort.status === 400) return "Die Eingabe wurde nicht angenommen.";
  if (antwort.status === 401 || antwort.status === 403)
    return "E-Mail-Adresse oder Passwort stimmen nicht.";
  if (antwort.status === 429) return "Zu viele Versuche. Bitte warte zehn Minuten.";
  if (antwort.status >= 500) return "Der Server hat gerade ein Problem. Bitte später erneut.";

  return (inhalt && inhalt.message) || "Etwas ist schiefgegangen.";
}

export class ServerFehler extends Error {
  constructor(meldung, status) {
    super(meldung);
    this.name = "ServerFehler";
    this.status = status;
  }
}

async function ruf(pfad, { methode = "GET", inhalt, token, formular } = {}) {
  const kopf = {};
  if (token) kopf.Authorization = token;
  if (inhalt && !formular) kopf["Content-Type"] = "application/json";

  let antwort;
  try {
    antwort = await fetch(`${SERVER}${pfad}`, {
      method: methode,
      headers: kopf,
      body: formular ? formular : inhalt ? JSON.stringify(inhalt) : undefined,
    });
  } catch (fehler) {
    throw new ServerFehler(
      "Keine Verbindung zum Server. Bist du im Netz?",
      0
    );
  }

  let daten = null;
  try {
    daten = await antwort.json();
  } catch {
    daten = null;
  }

  if (!antwort.ok) throw new ServerFehler(meldungAus(antwort, daten), antwort.status);
  return daten;
}

/* ---------------------------------------------------------------
   Anmelden und Registrieren
   --------------------------------------------------------------- */

export async function pruefeCode(code) {
  return ruf("/api/fb/code-pruefen", { methode: "POST", inhalt: { code } });
}

export async function registriere({ code, name, email, passwort }) {
  const daten = await ruf("/api/fb/registrieren", {
    methode: "POST",
    inhalt: { code, name, email, passwort },
  });
  merke(daten.token, daten.record);
  return daten;
}

export async function melde(email, passwort) {
  const daten = await ruf("/api/collections/users/auth-with-password", {
    methode: "POST",
    inhalt: { identity: email, password: passwort },
  });
  merke(daten.token, daten.record);
  return daten;
}

export async function setzePasswortNeu(code, passwort) {
  const daten = await ruf("/api/fb/passwort-neu", {
    methode: "POST",
    inhalt: { code, passwort },
  });
  merke(daten.token, daten.record);
  return daten;
}

/* Prüft beim Start, ob die gemerkte Anmeldung noch gilt. */
export async function frischeAnmeldung(token) {
  const daten = await ruf("/api/collections/users/auth-refresh", {
    methode: "POST",
    token,
  });
  merke(daten.token, daten.record);
  return daten;
}

/* ---------------------------------------------------------------
   Profil
   --------------------------------------------------------------- */

export async function speichereProfil(token, kontoId, { name, bild, bildEntfernen }) {
  const formular = new FormData();
  if (typeof name === "string") formular.append("name", name);
  if (bild) formular.append("avatar", bild);
  if (bildEntfernen) formular.append("avatar", "");

  const daten = await ruf(`/api/collections/users/records/${kontoId}`, {
    methode: "PATCH",
    token,
    formular,
  });
  merke(token, daten);
  return daten;
}

/* Adresse eines hinterlegten Bildes oder einer Datei. */
export function dateiAdresse(satz, feld) {
  const name = satz && satz[feld];
  if (!name) return null;
  return `${SERVER}/api/files/${satz.collectionId}/${satz.id}/${name}`;
}

/*
  ---------------------------------------------------------------
  Zugang zu geschützten Dateien
  ---------------------------------------------------------------

  Seit der Wanderung `1785330000_dateien_geschuetzt` ist `dateien.datei`
  geschützt: Der Server liefert die Datei nur noch mit einem kurzlebigen
  Dateitoken, das ein angemeldetes Konto anfordert.

  Das Token gilt drei Minuten. Erneuert wird nach zweieinhalb — knapp
  darunter, damit nie ein abgelaufenes in einer Adresse steht, und nicht
  bei jedem Bild neu, sonst käme bei zehn Bildern auf einer Folie zehnmal
  dieselbe Anfrage.
*/
const ZUGANG_GILT_MS = 150 * 1000;

let zugangsspeicher = { wert: null, bis: 0 };

export async function holeDateiZugang(token) {
  const jetzt = Date.now();
  if (zugangsspeicher.wert && jetzt < zugangsspeicher.bis) return zugangsspeicher.wert;

  const daten = await ruf("/api/files/token", { methode: "POST", token });
  zugangsspeicher = { wert: daten.token, bis: jetzt + ZUGANG_GILT_MS };
  return zugangsspeicher.wert;
}

/* Beim Abmelden ist das Token wertlos — und darf nicht liegen bleiben. */
export function vergissDateiZugang() {
  zugangsspeicher = { wert: null, bis: 0 };
}

/*
  Die Adresse einer geschützten Datei. `zugang` kommt aus `useDateiZugang`
  — ohne ihn gibt es keine Adresse, denn eine ohne Token führt nur zu einer
  Fehlermeldung im leeren Fenster.
*/
export function geschuetzteAdresse(zugang, satz, feld) {
  const grund = dateiAdresse(satz, feld);
  if (!grund || !zugang) return null;
  return `${grund}?token=${encodeURIComponent(zugang)}`;
}

/* ---------------------------------------------------------------
   Mitglieder der eigenen Fraktion
   --------------------------------------------------------------- */

export async function holeMitglieder(token) {
  const daten = await ruf(
    "/api/collections/users/records?perPage=200&sort=name",
    { token }
  );
  return daten.items || [];
}

/* ---------------------------------------------------------------
   Was von der Verwaltung kommt
   --------------------------------------------------------------- */

/*
  Sitzungen, Tagesordnung und Vorlagen in einem Rutsch.

  Drei Abfragen statt einer, weil PocketBase je Sammlung antwortet. Sie laufen
  gleichzeitig, nicht nacheinander — das ist auf einem Tablet im Mobilfunknetz
  der Unterschied zwischen einer und drei Wartezeiten.

  Zurückgezogene Vorlagen und abgesagte Sitzungen kommen bewusst mit: Die
  Fraktion muss sehen können, dass die Verwaltung etwas kassiert hat.
*/
export async function holeVerwaltungsdaten(token) {
  const abfrage = (pfad) => ruf(pfad, { token }).then((d) => (d && d.items) || []);

  const [sitzungen, tagesordnung, vorlagen] = await Promise.all([
    abfrage("/api/collections/sitzungen/records?perPage=200&sort=beginn"),
    abfrage("/api/collections/tagesordnungspunkte/records?perPage=500&sort=reihenfolge"),
    abfrage("/api/collections/vorlagen/records?perPage=500&sort=aktenzeichen"),
  ]);

  return { sitzungen, tagesordnung, vorlagen };
}

/* ---------------------------------------------------------------
   Dateien
   --------------------------------------------------------------- */

export async function holeDateien(token) {
  const daten = await ruf(
    "/api/collections/dateien/records?perPage=200&sort=-hochgeladen&expand=hochgeladen_von",
    { token }
  );
  return daten.items || [];
}

export async function ladeDateiHoch(token, { titel, datei, fraktionId, kontoId, nichtoeffentlich }) {
  const formular = new FormData();
  formular.append("titel", titel);
  formular.append("datei", datei);
  formular.append("fraktion", fraktionId);
  formular.append("hochgeladen_von", kontoId);
  formular.append("nichtoeffentlich", nichtoeffentlich ? "true" : "false");

  return ruf("/api/collections/dateien/records", {
    methode: "POST",
    token,
    formular,
  });
}

export async function loescheDatei(token, id) {
  return ruf(`/api/collections/dateien/records/${id}`, { methode: "DELETE", token });
}

export async function holeFraktion(token, id) {
  if (!id) return null;
  try {
    return await ruf(`/api/collections/fraktionen/records/${id}`, { token });
  } catch {
    return null;
  }
}

/* ---------------------------------------------------------------
   Markierungen und Notizen

   Ein Satz ist beides: ohne Text eine bloße Markierung, mit Text eine Notiz
   an die Fraktion. Welche man sehen darf, entscheidet der Server — private
   Notizen anderer kommen gar nicht erst herüber.
   --------------------------------------------------------------- */

/* Serversatz → die Form, die die Oberfläche seit jeher erwartet. */
function alsNotiz(satz) {
  return {
    id: satz.id,
    dateiId: satz.datei,
    absatzId: satz.absatz,
    start: satz.start || 0,
    ende: satz.ende || 0,
    zitat: satz.zitat || "",
    notiz: satz.notiz || "",
    sichtbarkeit: satz.sichtbarkeit || "fraktion",
    farbe: satz.farbe || "gelb",
    /*
      Bei einer Vorlage leer — dort genügen Start und Ende, weil der Text
      der Verwaltung sich nicht ändert. Bei einem gemeinsamen Dokument steht
      hier die von Yjs kodierte Stelle, die das Weiterschreiben übersteht.
    */
    verankerung: satz.verankerung || "",
    verfasserId: satz.konto,
    zeit: new Date(String(satz.angelegt || "").replace(" ", "T")),
  };
}

export async function holeNotizen(token) {
  const daten = await ruf(
    "/api/collections/notizen/records?perPage=500&sort=datei,absatz,start",
    { token }
  );
  return (daten.items || []).map(alsNotiz);
}

export async function legeNotizAn(token, { fraktionId, kontoId, notiz }) {
  const satz = await ruf("/api/collections/notizen/records", {
    methode: "POST",
    token,
    inhalt: {
      fraktion: fraktionId,
      konto: kontoId,
      datei: notiz.dateiId,
      absatz: notiz.absatzId,
      start: notiz.start,
      ende: notiz.ende,
      zitat: notiz.zitat,
      notiz: notiz.notiz || "",
      sichtbarkeit: notiz.sichtbarkeit || "fraktion",
      farbe: notiz.farbe || "gelb",
      verankerung: notiz.verankerung || "",
    },
  });
  return alsNotiz(satz);
}

export async function aendereNotiz(token, id, felder) {
  const satz = await ruf(`/api/collections/notizen/records/${id}`, {
    methode: "PATCH",
    token,
    inhalt: felder,
  });
  return alsNotiz(satz);
}

export async function loescheNotiz(token, id) {
  await ruf(`/api/collections/notizen/records/${id}`, { methode: "DELETE", token });
}

/* ---------------------------------------------------------------
   Die Arbeit der Fraktion: Meinungsbild, Teilnahme, Aufgaben

   Gelesen wird alles der eigenen Fraktion — das Meinungsbild ist
   ausdrücklich namentlich sichtbar. Geschrieben wird nur am Eigenen; den
   Stand einer Aufgabe darf jeder ändern, denn wer sie erledigt, ist selten
   der, der sie aufgeschrieben hat.
   --------------------------------------------------------------- */

function alsDatum(wert) {
  if (!wert) return null;
  const d = new Date(String(wert).replace(" ", "T"));
  return isNaN(d.getTime()) ? null : d;
}

function alsMeinung(satz) {
  return {
    id: satz.id,
    topId: satz.top,
    personId: satz.konto,
    position: satz.position,
    zeit: alsDatum(satz.geaendert || satz.angelegt) || new Date(),
  };
}

function alsTeilnahme(satz) {
  return {
    id: satz.id,
    sitzungId: satz.sitzung,
    personId: satz.konto,
    status: satz.status,
  };
}

function alsAufgabe(satz) {
  return {
    id: satz.id,
    titel: satz.titel,
    beschreibung: satz.beschreibung || "",
    topId: satz.top || null,
    vorlageId: satz.vorlage || null,
    faellig: alsDatum(satz.faellig),
    zustaendigId: satz.zustaendig || satz.konto,
    /* Wer mitliest, ohne zuständig zu sein. Wer anlegt, steht von selbst drin. */
    beobachterIds: Array.isArray(satz.beobachter) ? satz.beobachter : [],
    verfasserId: satz.konto,
    spalteId: satz.spalte || null,
    sichtbarkeit: satz.sichtbarkeit || "privat",
    reihenfolge: satz.reihenfolge || 0,
    /* Beides kommt als Liste; ein leeres Feld liefert null. */
    etiketten: Array.isArray(satz.etiketten) ? satz.etiketten : [],
    checkliste: Array.isArray(satz.checkliste) ? satz.checkliste : [],
  };
}

function alsSpalte(satz) {
  return {
    id: satz.id,
    name: satz.name,
    reihenfolge: satz.reihenfolge || 0,
    erledigt: Boolean(satz.erledigt),
  };
}

/*
  Ein Termin der Fraktion.

  `verknuepfungen` kommt als Liste aus { art, id } — leer, wenn nichts daran
  hängt. Ein leeres JSON-Feld liefert null, deshalb die Prüfung.
*/
function alsTermin(satz) {
  return {
    id: satz.id,
    titel: satz.titel,
    beschreibung: satz.beschreibung || "",
    beginn: alsDatum(satz.beginn),
    ende: alsDatum(satz.ende),
    ort: satz.ort || "",
    verfasserId: satz.konto,
    eingeladenIds: Array.isArray(satz.eingeladen) ? satz.eingeladen : [],
    verknuepfungen: Array.isArray(satz.verknuepfungen) ? satz.verknuepfungen : [],
  };
}

export async function holeArbeit(token) {
  const abfrage = (pfad) => ruf(pfad, { token }).then((d) => (d && d.items) || []);

  const [meinungen, teilnahmen, aufgaben, spalten, termine] = await Promise.all([
    abfrage("/api/collections/meinungsbilder/records?perPage=500"),
    abfrage("/api/collections/teilnahmen/records?perPage=500"),
    abfrage("/api/collections/aufgaben/records?perPage=500&sort=reihenfolge,faellig"),
    abfrage("/api/collections/spalten/records?perPage=100&sort=reihenfolge"),
    abfrage("/api/collections/termine/records?perPage=500&sort=beginn"),
  ]);

  return {
    meinungsbilder: meinungen.map(alsMeinung),
    teilnahmen: teilnahmen.map(alsTeilnahme),
    aufgaben: aufgaben.map(alsAufgabe),
    spalten: spalten.map(alsSpalte),
    termine: termine.map(alsTermin),
  };
}

/* ---------- Termine ---------- */

/*
  Wer einträgt, ist eingeladen. Sonst verlöre er seinen eigenen Termin in
  dem Augenblick, in dem er ihn anlegt — die Leseregel auf dem Server kennt
  nur „eingetragen oder eingeladen“, und das Eintragen soll keine
  Sonderstellung sein, die man später vergisst.
*/
export async function legeTerminAn(token, { fraktionId, kontoId, termin }) {
  const eingeladen = Array.from(new Set([kontoId, ...(termin.eingeladenIds || [])]));

  const satz = await ruf("/api/collections/termine/records", {
    methode: "POST",
    token,
    inhalt: {
      fraktion: fraktionId,
      konto: kontoId,
      titel: termin.titel,
      beschreibung: termin.beschreibung || "",
      beginn: new Date(termin.beginn).toISOString(),
      ende: termin.ende ? new Date(termin.ende).toISOString() : "",
      ort: termin.ort || "",
      eingeladen,
      verknuepfungen: termin.verknuepfungen || [],
    },
  });
  return alsTermin(satz);
}

export async function aendereTermin(token, id, felder) {
  const inhalt = {};

  if (felder.titel !== undefined) inhalt.titel = felder.titel;
  if (felder.beschreibung !== undefined) inhalt.beschreibung = felder.beschreibung;
  if (felder.ort !== undefined) inhalt.ort = felder.ort;
  if (felder.eingeladenIds !== undefined) inhalt.eingeladen = felder.eingeladenIds;
  if (felder.verknuepfungen !== undefined) inhalt.verknuepfungen = felder.verknuepfungen;
  if (felder.beginn !== undefined) inhalt.beginn = new Date(felder.beginn).toISOString();
  if (felder.ende !== undefined) {
    inhalt.ende = felder.ende ? new Date(felder.ende).toISOString() : "";
  }

  const satz = await ruf(`/api/collections/termine/records/${id}`, {
    methode: "PATCH",
    token,
    inhalt,
  });
  return alsTermin(satz);
}

export async function loescheTermin(token, id) {
  await ruf(`/api/collections/termine/records/${id}`, { methode: "DELETE", token });
}

/*
  Anlegen oder ändern in einem Aufruf.

  PocketBase kennt kein „upsert“. Ohne diese Hilfe stünde die Fallunter-
  scheidung an vier Stellen — und irgendwann wäre eine davon falsch.
*/
async function legeAnOderAendere(token, sammlung, vorhandeneId, inhalt) {
  if (vorhandeneId) {
    return ruf(`/api/collections/${sammlung}/records/${vorhandeneId}`, {
      methode: "PATCH",
      token,
      inhalt,
    });
  }
  return ruf(`/api/collections/${sammlung}/records`, { methode: "POST", token, inhalt });
}

export async function setzeMeinungsbildAufServer(token, { id, fraktionId, kontoId, topId, position }) {
  const satz = await legeAnOderAendere(token, "meinungsbilder", id, {
    fraktion: fraktionId,
    konto: kontoId,
    top: topId,
    position,
  });
  return alsMeinung(satz);
}

export async function entferneMeinungsbildVomServer(token, id) {
  await ruf(`/api/collections/meinungsbilder/records/${id}`, { methode: "DELETE", token });
}

export async function setzeTeilnahmeAufServer(token, { id, fraktionId, kontoId, sitzungId, status }) {
  const satz = await legeAnOderAendere(token, "teilnahmen", id, {
    fraktion: fraktionId,
    konto: kontoId,
    sitzung: sitzungId,
    status,
  });
  return alsTeilnahme(satz);
}

/*
  Wie viele Menschen hängen an dieser Aufgabe?

  Die zuständige Person und alle Beobachtenden, ohne Doppelungen. Genau
  daran entscheidet sich die Sichtbarkeit: Bei einem einzigen ist es ein
  Merkzettel, ab dem zweiten betrifft die Aufgabe mehrere und gehört ins
  Fraktionsboard.
*/
function beteiligte({ zustaendigId, beobachterIds }) {
  const alle = new Set(beobachterIds || []);
  if (zustaendigId) alle.add(zustaendigId);
  return alle;
}

export function sichtbarkeitFuer(aufgabe) {
  return beteiligte(aufgabe).size > 1 ? "fraktion" : "privat";
}

export async function legeAufgabeAn(token, { fraktionId, kontoId, aufgabe }) {
  const zustaendig = aufgabe.zustaendigId || kontoId;

  /*
    Wer eine Aufgabe anlegt, beobachtet sie. Sonst verlöre er sie in dem
    Augenblick aus dem eigenen Brett, in dem er sie weitergibt — und wer
    beim Bauamt nachfragen lässt, will wissen, ob die Antwort da ist.
    Austragen kann man sich später.
  */
  const beobachterIds = Array.from(new Set([kontoId, ...(aufgabe.beobachterIds || [])]));

  const satz = await ruf("/api/collections/aufgaben/records", {
    methode: "POST",
    token,
    inhalt: {
      fraktion: fraktionId,
      konto: kontoId,
      titel: aufgabe.titel,
      beschreibung: aufgabe.beschreibung || "",
      top: aufgabe.topId || "",
      vorlage: aufgabe.vorlageId || "",
      faellig: aufgabe.faellig ? new Date(aufgabe.faellig).toISOString() : "",
      zustaendig,
      beobachter: beobachterIds,
      spalte: aufgabe.spalteId || "",
      reihenfolge: aufgabe.reihenfolge || 0,
      etiketten: aufgabe.etiketten || [],
      checkliste: aufgabe.checkliste || [],
      sichtbarkeit: sichtbarkeitFuer({ zustaendigId: zustaendig, beobachterIds }),
    },
  });
  return alsAufgabe(satz);
}

/*
  Eine Aufgabe ändern.

  Die Sichtbarkeit wird nirgends von Hand gesetzt. Sie folgt daraus, wie
  viele Menschen an der Aufgabe hängen — sonst gäbe es zwei Wahrheiten
  darüber, wen eine Aufgabe angeht. Dasselbe Muster wie bei einer privaten
  Notiz, die in den Chat gegeben wird.

  Weil das aus dem GANZEN Datensatz folgt und nicht aus den geänderten
  Feldern allein, rechnet der Aufrufer sie aus und reicht sie in `nachher`
  herein — er kennt den bisherigen Stand.
*/
export async function aendereAufgabe(token, id, felder, nachher) {
  const inhalt = {};

  if (felder.titel !== undefined) inhalt.titel = felder.titel;
  if (felder.beschreibung !== undefined) inhalt.beschreibung = felder.beschreibung;
  if (felder.spalteId !== undefined) inhalt.spalte = felder.spalteId || "";
  if (felder.reihenfolge !== undefined) inhalt.reihenfolge = felder.reihenfolge;
  if (felder.etiketten !== undefined) inhalt.etiketten = felder.etiketten;
  if (felder.checkliste !== undefined) inhalt.checkliste = felder.checkliste;
  if (felder.zustaendigId !== undefined) inhalt.zustaendig = felder.zustaendigId;
  if (felder.beobachterIds !== undefined) inhalt.beobachter = felder.beobachterIds;
  if (felder.faellig !== undefined) {
    inhalt.faellig = felder.faellig ? new Date(felder.faellig).toISOString() : "";
  }

  const betrifftBeteiligung =
    felder.zustaendigId !== undefined || felder.beobachterIds !== undefined;
  if (betrifftBeteiligung && nachher) {
    inhalt.sichtbarkeit = sichtbarkeitFuer(nachher);
  }

  const satz = await ruf(`/api/collections/aufgaben/records/${id}`, {
    methode: "PATCH",
    token,
    inhalt,
  });
  return alsAufgabe(satz);
}

/* ---------- Spalten des Bretts ---------- */

export async function legeSpalteAn(token, { fraktionId, name, reihenfolge }) {
  const satz = await ruf("/api/collections/spalten/records", {
    methode: "POST",
    token,
    inhalt: { fraktion: fraktionId, name, reihenfolge: reihenfolge || 0 },
  });
  return alsSpalte(satz);
}

export async function aendereSpalte(token, id, felder) {
  const satz = await ruf(`/api/collections/spalten/records/${id}`, {
    methode: "PATCH",
    token,
    inhalt: felder,
  });
  return alsSpalte(satz);
}

export async function loescheSpalte(token, id) {
  await ruf(`/api/collections/spalten/records/${id}`, { methode: "DELETE", token });
}

export async function loescheAufgabe(token, id) {
  await ruf(`/api/collections/aufgaben/records/${id}`, { methode: "DELETE", token });
}

export async function entferneTeilnahme(token, id) {
  await ruf(`/api/collections/teilnahmen/records/${id}`, { methode: "DELETE", token });
}

/* ---------------------------------------------------------------
   Kalenderabo
   --------------------------------------------------------------- */

/*
  Der Schlüssel kommt über einen eigenen Endpunkt und nicht mit dem Konto:
  Am Konto wäre er für jedes Fraktionsmitglied lesbar, das die
  Mitgliederliste abruft — und damit wäre er keiner.
*/
export async function holeKalenderadresse(token) {
  const daten = await ruf("/api/fb/kalender-adresse", { token });
  return daten.schluessel;
}

export async function erneuereKalenderadresse(token) {
  const daten = await ruf("/api/fb/kalender-neu", { methode: "POST", token });
  return daten.schluessel;
}

/* ---------------------------------------------------------------
   Dokumente, die die Fraktion gemeinsam schreibt
   --------------------------------------------------------------- */

/*
  Yjs arbeitet mit rohen Bytes. Die Datenbank hält Text. Dazwischen steht
  Base64 — umständlich, aber die einzige Form, die durch JSON passt, ohne
  dass ein Byte kippt.
*/
export function alsBase64(bytes) {
  let roh = "";
  for (let i = 0; i < bytes.length; i += 1) roh += String.fromCharCode(bytes[i]);
  return btoa(roh);
}

export function ausBase64(text) {
  const roh = atob(text);
  const bytes = new Uint8Array(roh.length);
  for (let i = 0; i < roh.length; i += 1) bytes[i] = roh.charCodeAt(i);
  return bytes;
}

export async function holeDokumente(token) {
  const daten = await ruf(
    "/api/collections/dokumente/records?perPage=200&sort=-zuletzt_geschrieben&expand=konto",
    { token }
  );
  return daten.items || [];
}

export async function holeDokument(token, id) {
  return ruf(`/api/collections/dokumente/records/${id}?expand=konto`, { token });
}

export async function legeDokumentAn(token, { fraktionId, kontoId, titel, art }) {
  return ruf("/api/collections/dokumente/records", {
    methode: "POST",
    token,
    inhalt: {
      fraktion: fraktionId,
      konto: kontoId,
      titel,
      /* Heute gibt es nur Text; Tabellen und Präsentationen kommen später. */
      art: art || "dokument",
      /*
        Auch ein frisches Dokument gilt als gerade bearbeitet — sonst
        stünde es in Office ganz UNTEN statt oben, weil das Feld leer wäre
        und leere Werte beim Sortieren hinten landen. Genau das ist beim
        ersten Anlauf passiert: Ein eben angelegtes Dokument war in der
        Liste nirgends zu finden.
      */
      zuletzt_geschrieben: new Date().toISOString().replace("T", " "),
    },
  });
}

/*
  „Hier hat gerade jemand geschrieben."

  Nötig, weil das Tippen selbst den Dokument-Datensatz nicht anfasst — die
  Änderungen landen in `dokumentschritte`. Ohne diesen Anstoß bliebe ein
  Dokument, an dem eine Stunde gearbeitet wurde, in Office ganz unten
  stehen.

  Wird von der Oberfläche gedrosselt aufgerufen, höchstens alle halbe
  Minute.
*/
export async function beruehreDokument(token, id) {
  try {
    await ruf(`/api/collections/dokumente/records/${id}`, {
      methode: "PATCH",
      token,
      inhalt: { zuletzt_geschrieben: new Date().toISOString().replace("T", " ") },
    });
  } catch {
    /* Nur die Sortierung — dafür lohnt keine Fehlermeldung an den Nutzer. */
  }
}

/*
  Ein Dokument zum Muster erklären — oder zurücknehmen.

  Ein Muster ist kein anderer Datensatz, nur ein gekennzeichneter: Es wird
  geschrieben, kommentiert und gedruckt wie jedes andere Dokument. Der
  einzige Unterschied ist, dass in Office eine Kachel dafür steht, aus der
  neue Dokumente entstehen.
*/
export async function setzeMuster(token, id, muster) {
  return ruf(`/api/collections/dokumente/records/${id}`, {
    methode: "PATCH",
    token,
    inhalt: { muster: Boolean(muster) },
  });
}

/*
  Den Inhalt eines Musters in ein frisches Dokument übernehmen.

  Der Aufrufer baut den Yjs-Stand des Musters zusammen — dafür braucht es
  Yjs, und das gehört nicht in diese Datei, die ausschließlich mit dem
  Server spricht. Hier wird nur geschrieben.

  Es wird der VERDICHTETE Stand gesetzt und kein Schritt kopiert: Das neue
  Dokument fängt damit bei null an und schleppt nicht die
  Änderungsgeschichte des Musters mit — samt der Frage, wer davon was
  geschrieben hat.
*/
export async function setzeStand(token, id, stand) {
  return ruf(`/api/collections/dokumente/records/${id}`, {
    methode: "PATCH",
    token,
    inhalt: {
      stand: alsBase64(stand),
      stand_bis: new Date().toISOString().replace("T", " "),
    },
  });
}

export async function benenneDokument(token, id, titel) {
  return ruf(`/api/collections/dokumente/records/${id}`, {
    methode: "PATCH",
    token,
    inhalt: { titel },
  });
}

export async function loescheDokument(token, id) {
  await ruf(`/api/collections/dokumente/records/${id}`, { methode: "DELETE", token });
}

/*
  Die Schritte seit dem verdichteten Stand.

  `stand_bis` sagt, bis wohin der Stand reicht. Alles Jüngere muss zusätzlich
  eingespielt werden. Ohne diese Grenze läse man beim Öffnen jeden Schritt
  seit Anbeginn — und das sind nach einem Jahr Fraktionsarbeit zehntausende.
*/
export async function holeSchritte(token, dokumentId, ab) {
  const bedingungen = [`dokument='${dokumentId}'`];
  if (ab) bedingungen.push(`angelegt>'${ab}'`);
  const filter = encodeURIComponent(bedingungen.join(" && "));
  const daten = await ruf(
    `/api/collections/dokumentschritte/records?perPage=500&sort=angelegt&filter=${filter}`,
    { token }
  );
  return daten.items || [];
}

export async function sendeSchritt(token, { dokumentId, fraktionId, kontoId, daten }) {
  return ruf("/api/collections/dokumentschritte/records", {
    methode: "POST",
    token,
    inhalt: {
      dokument: dokumentId,
      fraktion: fraktionId,
      konto: kontoId,
      daten: alsBase64(daten),
    },
  });
}

/*
  Verdichten: den zusammengefassten Stand zurückschreiben und die Schritte
  wegräumen, die darin schon enthalten sind.

  Gefahrlos, weil Yjs-Änderungen beliebig oft und in beliebiger Reihenfolge
  angewendet werden dürfen. Zwei Geräte, die gleichzeitig verdichten,
  erzeugen denselben Stand; ein Schritt, der zweimal ankommt, ändert nichts.

  Erst der Stand, dann das Wegräumen — nie umgekehrt. Bräche die Verbindung
  dazwischen ab, wären sonst Änderungen weg, die nirgends stehen.
*/
export async function verdichte(token, dokumentId, { stand, bis, schritteIds }) {
  await ruf(`/api/collections/dokumente/records/${dokumentId}`, {
    methode: "PATCH",
    token,
    inhalt: { stand: alsBase64(stand), stand_bis: bis },
  });

  for (const id of schritteIds) {
    try {
      await ruf(`/api/collections/dokumentschritte/records/${id}`, {
        methode: "DELETE",
        token,
      });
    } catch {
      /* Schon weg — jemand anderes war schneller. Nicht schlimm. */
    }
  }
}

/* ---------- Wer gerade im Dokument ist ---------- */

export async function holePraesenz(token, dokumentId) {
  const filter = encodeURIComponent(`dokument='${dokumentId}'`);
  const daten = await ruf(
    `/api/collections/dokumentpraesenz/records?perPage=100&expand=konto&filter=${filter}`,
    { token }
  );
  return daten.items || [];
}

/*
  Ein Lebenszeichen. Je Dokument und Konto gibt es höchstens einen Satz —
  dafür sorgt ein eindeutiger Index in der Datenbank. Beim ersten Mal wird
  angelegt, danach überschrieben.

  ---------------------------------------------------------------
  Warum hier nachgesehen wird, statt einfach anzulegen
  ---------------------------------------------------------------

  Beim Verlassen des Dokuments wird der Satz gelöscht. Das gelingt aber nur,
  wenn die Anwendung dazu kommt — wer die Seite neu lädt, den Reiter
  schließt oder das Netz verliert, lässt seinen Satz liegen.

  Beim nächsten Öffnen ist die Satzkennung dann unbekannt, und ein Anlegen
  scheitert am eindeutigen Index. Vorher wurde dieser Fehler stillschweigend
  verschluckt: Der Herzschlag lief weiter, jeder Versuch endete mit 400, und
  der Betreffende erschien für die anderen NIE WIEDER als anwesend — ohne
  jede Meldung. Gefunden erst im Netzverkehr, nicht auf dem Bildschirm.

  Deshalb: Ist keine Satzkennung bekannt, wird zuerst nachgesehen, ob noch
  einer daliegt, und dieser überschrieben.
*/
async function sucheAnwesenheit(token, dokumentId, kontoId) {
  const filter = encodeURIComponent(`dokument='${dokumentId}' && konto='${kontoId}'`);
  try {
    const daten = await ruf(
      `/api/collections/dokumentpraesenz/records?perPage=1&filter=${filter}`,
      { token }
    );
    return (daten.items || [])[0] || null;
  } catch {
    return null;
  }
}

export async function meldeAnwesenheit(
  token,
  { dokumentId, fraktionId, kontoId, von, bis, farbe, satzId }
) {
  const inhalt = {
    dokument: dokumentId,
    fraktion: fraktionId,
    konto: kontoId,
    /*
      Die Stelle im Text als Zahlenpaar. Sind beide gleich, ist es eine
      Schreibmarke; stehen sie auseinander, hat der andere etwas ausgewählt.
    */
    von: Number.isFinite(von) ? von : 0,
    bis: Number.isFinite(bis) ? bis : Number.isFinite(von) ? von : 0,
    farbe: farbe || "",
  };

  if (satzId) {
    try {
      return await ruf(`/api/collections/dokumentpraesenz/records/${satzId}`, {
        methode: "PATCH",
        token,
        inhalt,
      });
    } catch {
      /* Satz weg — dann weiter unten neu anlegen. */
    }
  }

  /* Liegt noch einer von einer früheren Sitzung da? Dann diesen benutzen. */
  const alter = await sucheAnwesenheit(token, dokumentId, kontoId);
  if (alter) {
    try {
      return await ruf(`/api/collections/dokumentpraesenz/records/${alter.id}`, {
        methode: "PATCH",
        token,
        inhalt,
      });
    } catch {
      /* Inzwischen doch weg — dann neu anlegen. */
    }
  }

  return ruf("/api/collections/dokumentpraesenz/records", {
    methode: "POST",
    token,
    inhalt,
  });
}

export async function meldeAbwesenheit(token, satzId) {
  if (!satzId) return;
  try {
    await ruf(`/api/collections/dokumentpraesenz/records/${satzId}`, {
      methode: "DELETE",
      token,
    });
  } catch {
    /* Schon weg. */
  }
}
